import AddIcon from "@mui/icons-material/Add";
import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { isArray } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { AsyncSelectValue } from "../../../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../../../components/AsyncSelectScroll";
import CustomSelectScroll from "../../../../../../../components/CustomSelectScroll";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useRosTemplateById } from "../../../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../../../queries/template/new-flow/use-new-template-list";
import { useSpecialtiesList } from "../../../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DEFAULT_VALUE_LABEL_LIST,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../../../../utils/utils";
import { PeAddTemplateDrawer } from "../../../../../admin/library/templates/RosTemplateDrawer";
import { newRosTemplateInitialData } from "../../../../../admin/library/templates/template-constant";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { CarePlanTemplate } from "../../../../scheduling/appointment/encounter/encounter-constant";
import MaskMultiSelectScroll from "../../../../../../../components/MaskMultiSelectScroll";
import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { useNewTemplateCreate } from "../../../../../../../queries/template/new-flow/use-new-template-create";
import { useNewTemplateUpdate } from "../../../../../../../queries/template/new-flow/use-new-template-update";
import { useParams } from "react-router-dom";
// import { useParams } from "react-router-dom";

interface CarePlanTemplateModalProps {
  disclosureHost: DisclosureHost;
}

const CarePlanTemplateModal = memo(
  ({ disclosureHost }: CarePlanTemplateModalProps) => {
    const { mutateAsync: createTemplate } = useNewTemplateCreate();
    const { mutateAsync: updateTemplate } = useNewTemplateUpdate();

    const appointmentId = useQueryParams("appointmentid");
    const { patientId } = useParams();

    const queryClient = useQueryClient();

    const peTemplateDisc = useDisclosure();
    const [carePlan, setCarePlan] = useState<CarePlanTemplate | null>(null);
    const showSnackbar = useSnackbar();
    const [selectedTemplate, setSelectedTemplate] =
      useState<AsyncSelectValue | null>();
    const [speciality, setSpeciality] = useState<string | null>();
    const [searchKey, setSearchKey] = useDebounce<string>("");
    const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");
    const [specialityPagination, setSpecialityPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const [templatePagination, setTemplatePagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);

    const { data: specialities, isFetching: specialitiesFetching } =
      useSpecialtiesList({
        searchString: specialitySearch,
        xTENANTID: getXtenantId(),
        pagination: specialityPagination,
      });

    const specialtiesList = useMemo(
      () =>
        specialities?.data?.content?.map((it) => ({
          value: it?.uuid,
          label: it?.name,
        })),
      [specialities]
    );

    const { data, isLoading, isRefetching } = useNewTemplateList({
      enabled: disclosureHost.opened,
      pagination: templatePagination,
      gridSorting: [],
      searchKey,
      templateType: "CARE_PLAN",
      specialityUuids: speciality ? [speciality] : undefined,
      xTENANTID: getXtenantId(),
    });

    const { data: templateByIdData } = useRosTemplateById({
      rosTemplateUuid: selectedTemplate?.value,
      xTENANTID: getXtenantId(),
    });

    useEffect(() => {
      if (selectedTemplate?.value) {
        setCarePlan({
          templateName: templateByIdData?.data?.templateName ?? "",
          templateType: "CARE_PLAN",
          goals: templateByIdData?.data?.goals ?? "",
          icdCodes:
            transformMultiSelectFrontEndVal(
              DEFAULT_VALUE_LABEL_LIST,
              templateByIdData?.data?.icdCodes
            ) ?? [],
          intervention: templateByIdData?.data?.intervention ?? "",
          preventive_schedule:
            templateByIdData?.data?.preventive_schedule ?? "",
        });
      } else if (disclosureHost?.data) {
        setCarePlan(disclosureHost?.data as any);
      }
    }, [disclosureHost.opened, templateByIdData]);

    const handleSubmit = () => {
      const payload = {
        appointmentId: appointmentId,
        patientId,
        speciality: speciality ? { [speciality ?? ""]: "" } : undefined,
        goals: carePlan?.goals,
        icdCodes: carePlan?.icdCodes,
        intervention: carePlan?.intervention,
        preventive_schedule: carePlan?.preventive_schedule,
        templateTypes: "CARE_PLAN",
        templateName: carePlan?.templateName,
        uuid: disclosureHost?.data?.uuid ?? undefined,
      };
      const transformedPayload = {
        rosTemplate: {
          ...getUnNecessaryTransformData(["icdCodes"], payload),
        },
        xTENANTID: getXtenantId(),
      };

      (disclosureHost?.data
        ? updateTemplate(transformedPayload as any)
        : createTemplate(transformedPayload as any)
      )
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({
            queryKey: ["form-builder-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["form-builder-list"],
          });
          queryClient.invalidateQueries({
            queryKey: ["patient-careplan-list"],
          });

          setTimeout(closeAndReset, 10);
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    };

    const closeAndReset = () => {
      setCarePlan(null);
      setSelectedTemplate(null);
      setSpeciality(null);
      disclosureHost.close();
    };

    const rosTemplateList = useMemo(() => {
      return data?.data?.content?.map((it) => ({
        value: it.uuid,
        label: it?.templateName,
      }));
    }, [data]);

    const [searchIcdKey, setSearchIcdKey] = useDebounce<string>("");
    const [icdPagination, setIcdPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const { data: icdCodes, isFetching: isIcdFetching } = useGetCataLogCode({
      pagination: icdPagination,
      searchKey: searchIcdKey,
      type: "ICD",
      active: true,
      archive: false,
    });

    const transFormedIcdCodes = useMemo(() => {
      const fetched =
        icdCodes?.data?.content?.map((it) => ({
          value: it?.uuid,
          label: `${it?.code} - ${it?.description || ""}`,
        })) ?? [];
      const selected = (carePlan?.icdCodes ?? []) as { [k: string]: string }[];
      const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
      const missing = selected.filter(
        (s) => !fetched.some((o) => (o as any)[idKey] === s[idKey])
      );
      return [...missing, ...fetched];
    }, [icdCodes, carePlan?.icdCodes]);

    return (
      <StyledFormModal
        open={disclosureHost?.opened}
        onClose={closeAndReset}
        dialogTitle="Add Care Plan Template"
        dialogAction={
          <Flex gap={1}>
            <StyledButtonV2
              label="Save"
              variant="contained"
              onClick={handleSubmit}
            />
            <StyledButtonV2
              label="Cancel"
              variant="outlined"
              onClick={closeAndReset}
            />
          </Flex>
        }
        sx={{
          "& .MuiDialog-paper": {
            width: "800px",
            height: "65vh",
          },
        }}
      >
        <Flex flexDirection={"column"} gap={1} width={"100%"} pt={2}>
          {(isArray(disclosureHost.data) || !disclosureHost?.data) && (
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <CustomSelectScroll
                  label="Speciality"
                  placeholder="Speciality"
                  options={specialtiesList}
                  onSearch={(_, val) => {
                    setSpecialitySearch(val);
                    setSpecialityPagination((p) => ({ ...p, page: 0 }));
                  }}
                  value={speciality}
                  onChange={(v) => setSpeciality(v)}
                  pagination={specialityPagination}
                  setPagination={setSpecialityPagination}
                  totalElements={specialities?.data?.page?.totalElements}
                  loading={specialitiesFetching}
                />
              </Grid>
              <Grid item xs={7}>
                <AsyncSelectScroll
                  loading={isLoading || isRefetching}
                  placeholder="Select Template"
                  value={selectedTemplate}
                  options={rosTemplateList}
                  onSearch={(v) => {
                    setSearchKey(v);
                    setTemplatePagination((p) => ({ ...p, page: 0 }));
                  }}
                  onChange={(v) => setSelectedTemplate(v)}
                  pagination={templatePagination}
                  setPagination={setTemplatePagination}
                  totalElements={data?.data?.page?.totalElements}
                />
              </Grid>

              <Grid xs={1}>
                <Button
                  sx={{ marginLeft: 0.5, marginTop: 1 }}
                  variant="contained"
                  onClick={() =>
                    peTemplateDisc.open({
                      templateTypes: "CARE_PLAN",
                      rosTemplateTypeSet: newRosTemplateInitialData,
                    })
                  }
                >
                  <AddIcon />
                </Button>
              </Grid>
              <PeAddTemplateDrawer
                disclosureHost={peTemplateDisc}
                title="Add Care Plan Template"
              />
            </Grid>
          )}
          {((!carePlan && !disclosureHost?.data) ||
            (!carePlan && !disclosureHost?.data)) && (
            <Flex height={"60vh"}>
              <FlexCenterWrapper>{"Please Select Template"}</FlexCenterWrapper>
            </Flex>
          )}
          <Flex flexDirection={"column"} gap={1}>
            {carePlan && (
              <Flex flexDirection={"column"} gap={1}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <MaskMultiSelectScroll
                      // disabled={false}
                      placeholder="Search & Select ICD Codes"
                      value={(carePlan?.icdCodes as any) ?? undefined}
                      onSearch={setSearchIcdKey}
                      options={transFormedIcdCodes}
                      onChange={(v) =>
                        setCarePlan({ ...carePlan, icdCodes: v })
                      }
                      pagination={icdPagination}
                      setPagination={setIcdPagination}
                      totalElements={icdCodes?.data?.page?.totalElements}
                      loading={isIcdFetching}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextArea
                      label="Goals"
                      id="outlined-multiline-static"
                      rows={2}
                      variant="outlined"
                      name="goals"
                      placeholder="Enter Goals"
                      value={carePlan.goals}
                      onChange={(e) => {
                        setCarePlan({ ...carePlan, goals: e.target.value });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextArea
                      label="Template Name"
                      id="outlined-multiline-static"
                      rows={2}
                      variant="outlined"
                      name="templateName"
                      placeholder="Enter Template Name"
                      value={carePlan.templateName}
                      onChange={(e) => {
                        setCarePlan({
                          ...carePlan,
                          templateName: e.target.value,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextArea
                      label="Preventive Schedule"
                      id="outlined-multiline-static"
                      rows={2}
                      variant="outlined"
                      name="preventive_schedule"
                      placeholder="Enter Preventive Schedule"
                      value={carePlan.preventive_schedule}
                      onChange={(e) => {
                        setCarePlan({
                          ...carePlan,
                          preventive_schedule: e.target.value,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextArea
                      label="Intervention"
                      id="outlined-multiline-static"
                      rows={2}
                      variant="outlined"
                      name="intervention"
                      placeholder="Enter Intervention"
                      value={carePlan.intervention}
                      onChange={(e) => {
                        setCarePlan({
                          ...carePlan,
                          intervention: e.target.value,
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}></Grid>
                </Grid>
              </Flex>
            )}
          </Flex>
        </Flex>
      </StyledFormModal>
    );
  }
);

export default CarePlanTemplateModal;
